import "./insQueryStatedElement.scss";
import React, { useEffect, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWidgetMode } from "../hooks/use-current-widget";
import { ErrorType } from "./common/errorTypes";
import { InsProgressIndicator } from "./insProgressIndicator";
import { language } from "language/default";
import { setWidgetLoadStatusToTrueOrFalse } from "store/current-dashboard";
import { WidgetMode } from "store/current-dashboard/types";
import { setWidgetNoData } from "store/widgets-without-data";
const useLoadingIndicators = (dispatch, widgetLoadStatus, sectionId, widgetId, options) => {
    const { isErrorEmptyCurrency, isLoading, showNoData } = options;
    useEffect(() => {
        if (!widgetId) {
            return;
        }
        // When starting to load or when currency error is present, mark as not loaded (only when undefined)
        if ((isErrorEmptyCurrency || isLoading) && widgetLoadStatus === undefined) {
            dispatch(setWidgetLoadStatusToTrueOrFalse({ isLoaded: false, sectionId, widgetId }));
            return;
        }
        if (widgetLoadStatus === false) {
            const finishedSuccessfully = !isErrorEmptyCurrency && !isLoading && !showNoData;
            if (showNoData || finishedSuccessfully) {
                dispatch(setWidgetLoadStatusToTrueOrFalse({ isLoaded: true, sectionId, widgetId }));
            }
        }
    }, [dispatch, isErrorEmptyCurrency, isLoading, sectionId, showNoData, widgetId, widgetLoadStatus]);
};
// Extracted hook to keep the component concise and under the statements limit
const useInsQueryStatedElement = (props) => {
    const { widgetFactoryProps, queryState, hasData } = props;
    const widgetId = widgetFactoryProps?.widgetId ?? "";
    const sectionId = widgetFactoryProps?.sectionId ?? " ";
    const widgetMode = useWidgetMode(widgetId);
    const dispatch = useDispatch();
    const widgetLoadStatus = useSelector((state) => state.currentDashboard?.currentStatus
        ?.sections?.[widgetFactoryProps?.sectionId ?? ""]?.widgets?.[widgetFactoryProps?.widgetId ?? ""]?.isLoaded);
    const error = useSelector((state) => state.errors.widgetErrors[widgetId]);
    useEffect(() => {
        if (widgetMode === WidgetMode.Comparison && widgetId) {
            dispatch(setWidgetNoData({ value: !hasData, widgetId }));
        }
    }, [dispatch, hasData, widgetId, widgetMode]);
    const smartFilterLoading = useSelector((state) => state.currentDashboard?.smartFilterLoading);
    const isErrorEmptyCurrency = error === ErrorType.EmptyCurrencyException, isLoading = !queryState || queryState.isPending || !queryState.isFinished || smartFilterLoading === true, showNoData = hasData === false;
    useLoadingIndicators(dispatch, widgetLoadStatus, sectionId, widgetId, { isErrorEmptyCurrency, isLoading, showNoData });
    const emptyCurrencyContent = useMemo(() => React.createElement("div", { className: "centered-content" }, language.en.exceptions.emptyCurrencyException), []);
    const loadingContent = useMemo(() => React.createElement("div", { className: "loading-indicator" },
        React.createElement(InsProgressIndicator, { small: true })), []);
    const noDataContent = useMemo(() => React.createElement("div", { className: "centered-content" }, error ?? language.en.common.nodata), [error]);
    return { emptyCurrencyContent, isErrorEmptyCurrency, isLoading, loadingContent, noDataContent, showNoData };
};
const InsQueryStatedElementComponent = (props) => {
    const { isErrorEmptyCurrency, isLoading, showNoData, emptyCurrencyContent, loadingContent, noDataContent } = useInsQueryStatedElement(props);
    if (isErrorEmptyCurrency) {
        return emptyCurrencyContent;
    }
    if (isLoading) {
        return loadingContent;
    }
    if (showNoData) {
        return noDataContent;
    }
    return props.children;
};
export const InsQueryStatedElement = memo(InsQueryStatedElementComponent);
InsQueryStatedElement.displayName = "InsQueryStatedElement";
